import { useCallback, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Server-driven Stripe Checkout. The browser sends only { lookup_key, quantity,
 * origin_url } — never a price or amount. On success we redirect to the
 * Stripe-hosted checkout URL returned by the backend.
 */
export function useCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startCheckout = useCallback(async ({ lookup_key, quantity = 1, customer_email }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key,
          quantity,
          origin_url: window.location.origin,
          customer_email: customer_email || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Checkout failed (${res.status})`);
      }
      const data = await res.json();
      if (!data.checkout_url) throw new Error("No checkout URL returned");
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.message || "Checkout error");
      setLoading(false);
    }
  }, []);

  return { startCheckout, loading, error };
}

export async function fetchCatalog() {
  const res = await fetch(`${API}/api/payments/catalog`);
  if (!res.ok) throw new Error(`Catalog fetch failed (${res.status})`);
  return res.json();
}

export async function fetchStatus(sessionId) {
  const res = await fetch(`${API}/api/payments/status/${encodeURIComponent(sessionId)}`);
  if (!res.ok) throw new Error(`Status fetch failed (${res.status})`);
  return res.json();
}

export async function openPortalFromCheckout(sessionId) {
  const url = `${API}/api/portal/session-from-checkout/${encodeURIComponent(sessionId)}?origin=${encodeURIComponent(window.location.origin)}`;
  const res = await fetch(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Portal error (${res.status})`);
  }
  const data = await res.json();
  window.location.href = data.portal_url;
}

export async function openPortalByEmail(email) {
  const res = await fetch(`${API}/api/portal/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, origin_url: window.location.origin }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Portal error (${res.status})`);
  }
  const data = await res.json();
  window.location.href = data.portal_url;
}
